import { Controller } from "@hotwired/stimulus";

// Six boxes that behave as one field. The form posts `code`, a hidden input
// this controller keeps in step, so the server sees what it always saw.
//
// Without JavaScript the boxes are still six plain inputs and the hidden
// field stays empty, so the view renders `code` as a visible text field in
// that case; see verifications/new.html.erb.
export default class extends Controller {
  static targets = ["digit", "code"];

  connect() {
    // The plain field is the one that posts, and the one that stands in for
    // the boxes when this controller never runs. Once it does, the boxes are
    // the interface and it goes out of the way.
    this.codeTarget.hidden = true;
    this.codeTarget.required = false;
    this.digitTargets[0]?.focus();
  }

  // A paste of the whole code fills every box at once, which is how the code
  // actually arrives: copied from an e-mail.
  paste(event) {
    const digits = (event.clipboardData?.getData("text") ?? "").replace(/\D/g, "");
    if (!digits) return;

    event.preventDefault();
    this.digitTargets.forEach((box, i) => { box.value = digits[i] ?? ""; });
    this.sync();
    const next = this.digitTargets[Math.min(digits.length, this.digitTargets.length - 1)];
    next?.focus();
  }

  input(event) {
    const box = event.target;
    // A phone keyboard can deliver several characters at once; keep the first
    // and push the rest into the boxes after it.
    const digits = box.value.replace(/\D/g, "");
    box.value = digits[0] ?? "";

    const index = this.digitTargets.indexOf(box);
    digits.slice(1).split("").forEach((digit, i) => {
      const target = this.digitTargets[index + 1 + i];
      if (target) target.value = digit;
    });

    this.sync();
    if (box.value) this.digitTargets[Math.min(index + digits.length, this.digitTargets.length - 1)]?.focus();
  }

  // Backspace in an empty box steps back, which is what a caret would do in a
  // single field.
  keydown(event) {
    if (event.key !== "Backspace" || event.target.value) return;

    const previous = this.digitTargets[this.digitTargets.indexOf(event.target) - 1];
    if (!previous) return;

    event.preventDefault();
    previous.value = "";
    this.sync();
    previous.focus();
  }

  sync() {
    this.codeTarget.value = this.digitTargets.map((box) => box.value).join("");
  }
}
